import type { StoryElement } from '../types';

/**
 * Single frontend owner of the `base_score_details` blob's scoring shape.
 *
 * The server attaches a `base_score_details` JSON blob to every story. Turning
 * that raw blob into the scalar quantities the UI reasons about — the social
 * contribution (which carries a historical dual key spelling), the positive
 * source contribution, and the pre-decay subtotal — used to be re-derived
 * independently by the client-side ranking recompute (`feedScoring.ts`, since
 * removed with the legacy smart-feed channels) and `scoreBreakdown.ts` (the
 * admin breakdown modal). The two copies drifted: the ranking recompute folded
 * `curator_adjustment` into the subtotal (matching the backend) while
 * `scoreBreakdown` silently dropped it, so a curated article's "Subtotal
 * before age" no longer multiplied out to its base score.
 *
 * This module owns that translation in one place, mirroring the backend's
 * `StoredScoreComponents` / `compute_overall_score`
 * (`backend/app/services/scoring_rules_service.py`) and the dual-spelling owner
 * `read_social_component` (`backend/app/queries/base_score_details_keys.py`).
 * The Python and TypeScript stacks cannot share a constant, so this is the
 * documented mirror the backend module refers to — both frontend consumers
 * compose these helpers instead of re-deriving the fallbacks and diverging.
 * The frontend only *displays* these quantities; ranking (and any recompute
 * of base or feed scores) is the backend's job.
 * The mobile apps carry the same decomposition as their own single owners:
 * `ios/Kingfisher/ScoreBreakdownMath.swift` and
 * `android/.../ui/reader/ScoreBreakdownMath.kt` — a change here must land
 * there too (and vice versa). The component list and signs of
 * `preDecaySubtotal`, and `readSocialComponent`'s key order, are parity-pinned
 * to the backend owner (`PRE_DECAY_COMPONENT_SIGNS` /
 * `SOCIAL_COMPONENT_READ_ORDER`) by
 * `backend/tests/test_pre_decay_mirror_parity.py`, so drift fails CI.
 */

/** The blob shape a story carries once its `base_score_details` is non-null. */
export type BaseScoreDetails = NonNullable<StoryElement['base_score_details']>;

/**
 * Read the social contribution out of a blob, honouring the historical dual
 * spelling in one place.
 *
 * Recent scorers write `social_adjustment`; legacy blobs carry `social_boost`
 * for the same quantity. Mirrors `SOCIAL_COMPONENT_READ_ORDER` on the backend:
 * prefer the recent key, fall back to the legacy one, else neutral. Uses
 * nullish coalescing so a present zero reads as zero (not as "missing"), which
 * matches how every real blob — where both keys are written together — scores.
 *
 * The same dual spelling exists on the flat story row (the server projects
 * both fields), so this accepts anything carrying the two keys — the blob or
 * the story itself — and every consumer honours the read order instead of
 * picking one spelling and going blank on rows that only carry the other.
 */
export function readSocialComponent(d: {
  social_adjustment?: number | null;
  social_boost?: number | null;
}): number {
  return d.social_adjustment ?? d.social_boost ?? 0;
}

/**
 * The positive source contribution that feeds the pre-decay subtotal.
 *
 * Only the positive part of `source_adjustment` adds to the score; its negative
 * part is already folded into the stored `adj_penalty`. Mirrors
 * `StoredScoreComponents.source_boost` on the backend.
 */
export function sourceBoost(d: BaseScoreDetails): number {
  return Math.max(d.source_adjustment ?? 0, 0);
}

/** The resolved components that sum to the pre-decay subtotal. */
export interface PreDecayComponents {
  tier: number;
  effectiveBoost: number;
  sourceBoost: number;
  socialBoost: number;
  curatorAdjustment: number;
  gatedPenalty: number;
  adjPenalty: number;
}

/**
 * The (tier + boosts − penalties) sum that is multiplied by `time_decay` to
 * produce the base score. This is the one definition of *which* components,
 * with *which* signs, make up the subtotal — including `curator_adjustment`.
 *
 * Mirrors `compute_overall_score` on the backend, which multiplies this by the
 * decay to rank; `scoreBreakdown` shows it as "Subtotal before age".
 * Callers pass already-resolved numbers so each keeps its own defaulting for
 * missing/optional fields.
 */
export function preDecaySubtotal(c: PreDecayComponents): number {
  return (
    c.tier +
    c.effectiveBoost +
    c.sourceBoost +
    c.socialBoost +
    c.curatorAdjustment -
    c.gatedPenalty -
    c.adjPenalty
  );
}
